/** SUNTRONIX ONE — Operations → Employee Fulfilment (customer-owned devices). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, PackageCheck } from "lucide-react";
import { Modal, Field } from "./StockInventoryDialogs";
import { PersonName } from "@/components/identity/Identity";
import { BillingTab, MonthlyReportTab } from "./CustomerOwnedBilling";

const inputCls = "mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5";
const NEXT = { REQUESTED: "RESERVED", RESERVED: "CONFIGURATION_PENDING",
  CONFIGURATION_PENDING: "READY_TO_SHIP", READY_TO_SHIP: "DISPATCHED",
  DISPATCHED: "DELIVERED" };
const VIEWS = ["ALL", "REQUESTED", "RESERVED", "CONFIGURATION_PENDING",
  "READY_TO_SHIP", "DISPATCHED", "DELIVERED"];

const TOP_TABS = ["Fulfilments", "Billing", "Monthly Report"];

export default function EmployeeFulfilment() {
  const [tab, setTab] = useState("Fulfilments");
  const [items, setItems] = useState([]);
  const [view, setView] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [upcoming, setUpcoming] = useState(0);

  const load = useCallback(() =>
    api.get("/inventory/fulfilments", { params: view !== "ALL" ? { stage: view } : {} })
      .then(({ data }) => { setItems(data.items || []); setUpcoming(data.upcoming_joiners || 0); }), [view]);
  useEffect(() => { load(); }, [load]);

  const advance = async (f) => {
    try {
      await api.post(`/inventory/fulfilments/${f.id}/advance`, { to_stage: NEXT[f.stage] });
      toast.success(`${f.code} → ${NEXT[f.stage].replace(/_/g, " ")}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="employee-fulfilment-page">
      <header className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-900 text-white grid place-items-center">
          <PackageCheck className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-xl font-heading font-semibold">Employee Fulfilment</div>
          <div className="text-[11px] text-slate-500">
            Customer-owned devices → employee joiners · {upcoming} upcoming joiner(s)
          </div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="ef-new"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-blue-800 text-white inline-flex items-center gap-1">
          <UserPlus className="h-3 w-3" /> New Fulfilment
        </button>
      </header>
      <div className="flex items-center gap-0 border-b border-slate-200">
        {TOP_TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`ef-tab-${t.replace(/ /g, "-")}`}
              className={"text-[11.5px] px-3 py-2 border-b-2 -mb-px " +
                (tab === t ? "border-blue-800 text-blue-900 font-semibold"
                           : "border-transparent text-slate-500 hover:text-slate-800")}>
            {t}</button>))}
      </div>
      {tab === "Billing" && <BillingTab />}
      {tab === "Monthly Report" && <MonthlyReportTab />}
      {tab === "Fulfilments" && <>
      <div className="flex items-center gap-1 flex-wrap">
        {VIEWS.map((v) => (
          <button key={v} onClick={() => setView(v)}
              data-testid={`ef-view-${v}`}
              className={"text-[10.5px] px-2.5 py-1 rounded-full border " +
                (view === v ? "bg-slate-900 text-white border-slate-900"
                            : "border-slate-300 text-slate-600 hover:bg-slate-50")}>
            {v.replace(/_/g, " ")}
          </button>))}
      </div>
      <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Fulfilment</th>
            <th className="text-left px-3 py-2">Customer / Employee</th>
            <th className="text-left px-3 py-2">Joining</th>
            <th className="text-left px-3 py-2">Devices</th>
            <th className="text-left px-3 py-2">Services / Autopilot</th>
            <th className="text-left px-3 py-2">Stage</th>
            <th className="text-right px-3 py-2 w-44"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 px-3 py-8">
              No fulfilments in this view.</td></tr>}
            {items.map((f) => (
              <tr key={f.id} className="border-t border-slate-100" data-testid={`ef-row-${f.code}`}>
                <td className="px-3 py-2 font-mono text-[10px]">{f.code}</td>
                <td className="px-3 py-2"><b>{f.customer_name}</b>
                  <div className="text-[10px] text-slate-500"><PersonName name={f.employee_name} className="text-slate-700" /> · {f.department || "—"}</div></td>
                <td className="px-3 py-2 font-mono text-[10px]">{f.joining_date || "—"}</td>
                <td className="px-3 py-2 text-[10px]">
                  {(f.devices || []).map((d) => (
                    <div key={d.unit_id} className="font-mono">
                      {d.serial}
                      {d.apple_external && <span className="ml-1 text-[8px] px-1 rounded bg-slate-200 text-slate-700"
                          title="ABM/ADE responsibility: Customer / Original supplier">ABM: NOT IN SCOPE</span>}
                    </div>))}
                  {!(f.devices || []).length && <span className="text-amber-700">device required</span>}
                </td>
                <td className="px-3 py-2 text-[10px]">
                  {(f.services || []).filter((s) => s.mode !== "NOT_REQUIRED").map((s) =>
                    <div key={s.service_id}>{s.service_name}
                      {s.mode === "CHARGEABLE" && <span className="ml-1 text-[8px] px-1 rounded bg-amber-100 text-amber-800">₹</span>}
                    </div>)}
                  {f.autopilot_status !== "NOT_REQUESTED" && (
                    <span className="text-[8.5px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800"
                        data-testid={`ef-autopilot-${f.code}`}>AUTOPILOT: {f.autopilot_status}</span>)}
                </td>
                <td className="px-3 py-2">
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">
                    {f.stage.replace(/_/g, " ")}</span></td>
                <td className="px-3 py-2 text-right space-x-1">
                  {NEXT[f.stage] && (
                    <button onClick={() => advance(f)} data-testid={`ef-advance-${f.code}`}
                        className="text-[10px] px-2 py-1 rounded bg-slate-900 text-white">
                      → {NEXT[f.stage].replace(/_/g, " ")}</button>)}
                  {!["DISPATCHED", "DELIVERED", "CANCELLED"].includes(f.stage) && (
                    <button onClick={async () => {
                        if (!window.confirm(`Cancel ${f.code} and release devices?`)) return;
                        try { await api.post(`/inventory/fulfilments/${f.id}/cancel`);
                          toast.success("Cancelled — devices released"); load();
                        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }}
                        data-testid={`ef-cancel-${f.code}`}
                        className="text-[10px] px-2 py-1 rounded border border-rose-400 text-rose-700">✕</button>)}
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>
      {open && <FulfilmentDialog onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); load(); }} />}
      </>}
    </div>
  );
}

function FulfilmentDialog({ onClose, onDone }) {
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ customer_id: "", customer_name: "", employee_id: "",
    employee_name: "", employee_email: "", employee_mobile: "", joining_date: "",
    department: "", designation: "", country: "", delivery_address: "", city: "",
    state: "", postal_code: "", delivery_contact: "", required_delivery_date: "",
    special_instructions: "" });
  const [emps, setEmps] = useState([]);
  const [empQ, setEmpQ] = useState("");
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [devices, setDevices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [services, setServices] = useState([]);
  const [svcModes, setSvcModes] = useState({});

  useEffect(() => {
    api.get("/inventory/customers", { params: { q: "" } }).then(({ data }) => setCustomers(data.items || []));
    api.get("/inventory/fulfilments/services").then(({ data }) => setServices(data.items || []));
  }, []);
  useEffect(() => {
    if (!f.customer_id) return;
    api.get("/inventory/fulfilments/propose", { params: { customer_id: f.customer_id } })
      .then(({ data }) => setDevices(data.items || []));
  }, [f.customer_id]);
  useEffect(() => {
    const t = setTimeout(() =>
      api.get("/inventory/fulfilments/employees", { params: { q: empQ } })
        .then(({ data }) => setEmps(data.items || [])), 250);
    return () => clearTimeout(t);
  }, [empQ]);

  const addEmp = async () => {
    try {
      const { data } = await api.post("/inventory/fulfilments/employees", {
        name: f.employee_name, email: f.employee_email, mobile: f.employee_mobile,
        department: f.department, designation: f.designation, country: f.country,
        joining_date: f.joining_date, company_id: f.customer_id });
      setF({ ...f, employee_id: data.employee.id });
      toast.success(data.existing ? "Existing employee matched by email" : "Employee added");
      setShowAddEmp(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submit = async () => {
    if (!f.customer_id || !f.employee_id || !f.employee_name) {
      toast.error("Customer and employee required"); return; }
    try {
      const { data } = await api.post("/inventory/fulfilments", {
        ...f, unit_ids: selected,
        services: services.filter((s) => svcModes[s.id] && svcModes[s.id] !== "NOT_REQUIRED")
          .map((s) => ({ service_id: s.id, service_name: s.service_name, mode: svcModes[s.id] })) });
      toast.success(`Fulfilment ${data.fulfilment.code} created (${data.fulfilment.stage})`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Modal title="New Employee Fulfilment" onClose={onClose} wide>
      <div className="grid grid-cols-3 gap-3" data-testid="fulfilment-dialog">
        <Field label="Customer" required>
          <select value={f.customer_id} data-testid="ef-customer"
              onChange={(e) => {
                const c = customers.find((x) => x.id === e.target.value);
                setF({ ...f, customer_id: e.target.value, customer_name: c?.name || "" });
                setSelected([]); }}
              className={inputCls}>
            <option value="">— pick —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></Field>
        <Field label="Employee (search or add)" required>
          <input value={empQ} onChange={(e) => setEmpQ(e.target.value)}
              data-testid="ef-employee-search" placeholder="Search name/email…" className={inputCls} />
          {emps.length > 0 && !f.employee_id && (
            <div className="border border-slate-200 rounded mt-1 max-h-28 overflow-y-auto">
              {emps.map((e2) => (
                <button key={e2.id} type="button"
                    onClick={() => setF({ ...f, employee_id: e2.id, employee_name: e2.name,
                      employee_email: e2.email || "", joining_date: e2.joining_date || f.joining_date })}
                    data-testid={`ef-emp-opt-${e2.id}`}
                    className="w-full text-left px-2 py-1 text-[11px] hover:bg-slate-50">
                  {e2.name} <span className="text-slate-400">{e2.email}</span>
                </button>))}
            </div>)}
          {f.employee_id && <div className="text-[10px] text-emerald-700 mt-1">✓ {f.employee_name}</div>}
        </Field>
        <div className="flex items-end pb-1">
          <button type="button" onClick={() => setShowAddEmp(!showAddEmp)}
              data-testid="ef-add-employee"
              className="text-[10.5px] px-2 py-1.5 rounded border border-slate-300">+ Add Employee</button>
        </div>
        {showAddEmp && (
          <div className="col-span-3 border border-dashed border-slate-300 rounded p-2 grid grid-cols-4 gap-2">
            {[["employee_name", "Name *"], ["employee_email", "Email"], ["employee_mobile", "Mobile"],
              ["department", "Department"], ["designation", "Designation"], ["country", "Country"]].map(([k, l]) => (
              <Field key={k} label={l}>
                <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                    data-testid={`ef-new-${k}`} className={inputCls} /></Field>))}
            <div className="flex items-end">
              <button type="button" onClick={addEmp} data-testid="ef-save-employee"
                  className="text-[10.5px] px-2.5 py-1.5 rounded bg-slate-900 text-white">Save employee</button>
            </div>
          </div>)}
        {[["joining_date", "Joining date", "date"], ["required_delivery_date", "Required delivery", "date"],
          ["delivery_contact", "Delivery contact", "text"], ["delivery_address", "Delivery address", "text"],
          ["city", "City", "text"], ["postal_code", "Postal code", "text"]].map(([k, l, t]) => (
          <Field key={k} label={l}>
            <input type={t} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                data-testid={`ef-${k}`} className={inputCls} /></Field>))}
        <div className="col-span-3">
          <div className="text-[10px] text-slate-500 mb-1">
            Proposed devices (this customer's available stock only)</div>
          <div className="flex flex-wrap gap-1" data-testid="ef-device-list">
            {devices.map((d) => (
              <button key={d.id} type="button"
                  onClick={() => setSelected((s) => s.includes(d.id) ? s.filter((x) => x !== d.id) : [...s, d.id])}
                  data-testid={`ef-device-${d.serial}`}
                  className={"text-[10px] font-mono px-2 py-1 rounded border " +
                    (selected.includes(d.id) ? "bg-blue-900 text-white border-blue-900"
                                             : "border-slate-300 hover:bg-slate-50")}>
                {d.serial} · {d.product_name?.slice(0, 28)}
                {d.apple_external ? " ·  ABM" : ""}
              </button>))}
            {!devices.length && f.customer_id &&
              <span className="text-[10.5px] text-amber-700">No available customer-owned stock — request will queue as DEVICE REQUIRED.</span>}
          </div>
        </div>
        <div className="col-span-3">
          <div className="text-[10px] text-slate-500 mb-1">Services (from Service Master)</div>
          <div className="space-y-1 max-h-36 overflow-y-auto border border-slate-100 rounded p-2">
            {services.map((s) => (
              <div key={s.id} className="flex items-center gap-2 text-[11px]" data-testid={`ef-svc-${s.id}`}>
                <span className="flex-1">{s.service_name}</span>
                {["NOT_REQUIRED", "INCLUDED", "CHARGEABLE"].map((m) => (
                  <button key={m} type="button"
                      onClick={() => setSvcModes({ ...svcModes, [s.id]: m })}
                      className={"text-[9px] px-1.5 py-0.5 rounded border " +
                        ((svcModes[s.id] || "NOT_REQUIRED") === m
                          ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-500")}>
                    {m.replace(/_/g, " ")}</button>))}
              </div>))}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={submit} data-testid="ef-submit"
            className="text-[11px] px-3 py-1.5 rounded-md bg-blue-800 text-white">Create Fulfilment</button>
      </div>
    </Modal>
  );
}
